import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { Sparkline } from '../../../components/Sparkline.js';
import { SentimentIndicator, type SentimentDirection } from './SentimentIndicator.js';

export type StorylineDirection = SentimentDirection;

interface SimplifiedIntelligenceCardProps {
  title: string;
  ticker?: string | null;
  sparkline?: number[];
  sparklineColor?: string;
  direction: StorylineDirection;
  onClick?: () => void;
  onTickerClick?: (ticker: string) => void;
}

const DIRECTION_ACCENT: Record<StorylineDirection, string> = {
  positive: '#63F1C8',
  neutral: '#B597FF',
  negative: '#FF9B9B',
};

export const SimplifiedIntelligenceCard: React.FC<SimplifiedIntelligenceCardProps> = ({
  title,
  ticker,
  sparkline,
  sparklineColor,
  direction,
  onClick,
  onTickerClick,
}) => {
  const accent = DIRECTION_ACCENT[direction];

  return (
    <Box
      onClick={onClick}
      sx={{
        position: 'relative',
        p: 2,
        cursor: onClick ? 'pointer' : 'default',
        '&:not(:last-child)::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: '10%',
          width: '80%',
          height: '1px',
          background: 'linear-gradient(90deg, transparent 0%, rgba(184, 137, 255, 0.4) 50%, transparent 100%)',
        },
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.02)',
        },
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              lineHeight: 1.4,
              color: '#E2E8F0',
              flex: 1,
            }}
          >
            {title}
          </Typography>
          <SentimentIndicator direction={direction} size={10} />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0}>
          {ticker ? (
            <Box
              onClick={(event) => {
                event.stopPropagation();
                onTickerClick?.(ticker);
              }}
              sx={{
                px: 1.5,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                borderTopLeftRadius: 4,
                borderBottomLeftRadius: 4,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#94A3B8',
                backgroundColor: 'rgba(255,255,255,0.05)',
                cursor: 'pointer',
                '&:hover': {
                  color: '#F8FAFC',
                  backgroundColor: 'rgba(255,255,255,0.08)',
                },
              }}
            >
              {ticker}
            </Box>
          ) : null}
          
          <Box
            sx={{
              height: 32,
              width: 80,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.05)', // Match ticker bg
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              px: 1,
              ml: '1px', // Tiny gap or remove for seamless look
            }}
          >
            {sparkline && sparkline.length ? (
              <Sparkline
                data={sparkline}
                color={sparklineColor ?? accent}
                width={60}
                height={20}
              />
            ) : (
              <Box sx={{ width: 60, height: 1, bgcolor: 'rgba(255,255,255,0.1)' }} />
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};
